// Flexible connection & curtailment (spec Feature 6).
//
// Rendered for every front-of-meter revenue model (Merchant, PPA, all tolling
// structures) and NEVER for Behind-the-Meter: peak shaving and ToU savings come
// from a site load, not from injected energy, so a grid curtailment instruction
// does not apply to them.
//
// The section is written to make the accuracy gate visible while the user is
// typing rather than after they have generated a report: the "what the report
// will do" strip at the bottom states, in plain words, whether the figure they
// entered will reach the base case or be shown as a labelled scenario.
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MaybeTooltip from "./MaybeTooltip";
import {
  CONNECTION_TYPE_OPTIONS,
  CURTAILMENT_INPUT_MODE_OPTIONS,
  CURTAILMENT_SOURCE_OPTIONS,
  SECURITIES_TREATMENT_OPTIONS,
  CONNECTION_CURTAILMENT_HELPER_TEXT,
  REGULATORY_CAP_NOTE,
  appliedCurtailmentFraction,
  connectionCurtailmentApplies,
  connectionCurtailmentNotices,
  curtailmentFraction,
  isEvidencedSource,
  showsSensitivityBand,
  validateConnectionCurtailment,
} from "./config/connectionCurtailment";

function pct(fraction) {
  return `${(fraction * 100).toFixed(fraction * 100 >= 10 ? 0 : 1).replace(/\.0$/, "")}%`;
}

export default function ConnectionCurtailmentSection({
  value,
  onChange,
  settlementType,
  regulatoryCapEnabled = false,
}) {
  if (!connectionCurtailmentApplies(settlementType)) return null;

  const cc = value || {};
  const errors = validateConnectionCurtailment(cc, settlementType);
  const notices = connectionCurtailmentNotices(cc);
  const stated = curtailmentFraction(cc);
  const applied = appliedCurtailmentFraction(cc);
  const evidenced = isEvidencedSource(cc.curtailmentSource);
  const band = showsSensitivityBand(cc);
  const byHours = cc.curtailmentInputMode === "hours_per_year";

  const set = (key) => (event) =>
    onChange({ ...cc, [key]: event.target.value });
  const setNumber = (key) => (event) =>
    onChange({ ...cc, [key]: event.target.value === "" ? "" : Number(event.target.value) });

  const selectedType =
    CONNECTION_TYPE_OPTIONS.find((option) => option.value === cc.connectionType) ||
    CONNECTION_TYPE_OPTIONS[0];
  const selectedTreatment =
    SECURITIES_TREATMENT_OPTIONS.find((option) => option.value === cc.securitiesTreatment) ||
    SECURITIES_TREATMENT_OPTIONS[0];

  let outcome;
  if (applied > 0) {
    outcome =
      `The base case will carry ${pct(applied)} curtailment. It is applied to injected ` +
      "energy before revenue, so it moves the energy balance, the LCOE and the cash flow " +
      "together.";
  } else if (band) {
    outcome =
      "The base case will carry no curtailment. Because a non-firm connection is declared " +
      "without an evidenced figure, the report adds a labelled 0 / 3 / 5 % scenario band " +
      "and states that it is an assumption, not a forecast.";
  } else if (stated > 0) {
    outcome =
      `The ${pct(stated)} you entered will NOT reduce the base case, because it has no ` +
      "connection offer or grid study behind it. Change the source to bring it into the numbers.";
  } else {
    outcome = "The base case will carry no curtailment, and the report will say so explicitly.";
  }

  return (
    <Accordion defaultExpanded={false}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Connection &amp; curtailment
        </Typography>
        <MaybeTooltip title={CONNECTION_CURTAILMENT_HELPER_TEXT}>
          <InfoOutlinedIcon fontSize="small" sx={{ ml: 1, opacity: 0.6 }} />
        </MaybeTooltip>
      </AccordionSummary>

      <AccordionDetails>
        <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
          {CONNECTION_CURTAILMENT_HELPER_TEXT}
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 320 }}>
            <InputLabel>Connection type</InputLabel>
            <Select
              label="Connection type"
              value={cc.connectionType ?? "unspecified"}
              onChange={set("connectionType")}
            >
              {CONNECTION_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{selectedType.description}</FormHelperText>
          </FormControl>

          <TextField
            size="small"
            label="Expected COD"
            placeholder="YYYY-MM-DD"
            value={cc.expectedCodDate ?? ""}
            onChange={set("expectedCodDate")}
            error={Boolean(errors.expectedCodDate)}
            helperText={errors.expectedCodDate || "Optional. Shown in Project Overview."}
            sx={{ minWidth: 200 }}
          />
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel>Curtailment stated as</InputLabel>
            <Select
              label="Curtailment stated as"
              value={cc.curtailmentInputMode ?? "percent_of_annual_energy"}
              onChange={set("curtailmentInputMode")}
            >
              {CURTAILMENT_INPUT_MODE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {byHours ? (
            <TextField
              size="small"
              type="number"
              label="Expected curtailment"
              value={cc.curtailmentHoursPerYear ?? 0}
              onChange={setNumber("curtailmentHoursPerYear")}
              error={Boolean(errors.curtailmentHoursPerYear)}
              helperText={
                errors.curtailmentHoursPerYear ||
                (stated > 0 ? `= ${pct(stated)} of annual injected energy` : "Hours per year")
              }
              InputProps={{ endAdornment: <InputAdornment position="end">h/yr</InputAdornment> }}
              sx={{ minWidth: 220 }}
            />
          ) : (
            <TextField
              size="small"
              type="number"
              label="Expected curtailment"
              value={cc.curtailmentPct ?? 0}
              onChange={setNumber("curtailmentPct")}
              error={Boolean(errors.curtailmentPct)}
              helperText={errors.curtailmentPct || "Share of annual injected energy"}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              sx={{ minWidth: 220 }}
            />
          )}

          <FormControl size="small" sx={{ minWidth: 300 }}>
            <InputLabel>Source of the curtailment figure</InputLabel>
            <Select
              label="Source of the curtailment figure"
              value={cc.curtailmentSource ?? "not_provided"}
              onChange={set("curtailmentSource")}
            >
              {CURTAILMENT_SOURCE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                  {option.evidenced ? "" : "  — scenario only"}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Only the connection offer or a grid study lets the figure reach the base case.
            </FormHelperText>
          </FormControl>

          <TextField
            size="small"
            label="Document reference"
            placeholder="HEDNO connection offer 2026-03-11, clause 7.2"
            value={cc.curtailmentSourceReference ?? ""}
            onChange={set("curtailmentSourceReference")}
            error={Boolean(errors.curtailmentSourceReference)}
            helperText={
              errors.curtailmentSourceReference ||
              (evidenced ? "Printed next to the figure in the report." : "Optional until a source is cited.")
            }
            sx={{ minWidth: 360, flexGrow: 1 }}
          />
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
          <TextField
            size="small"
            type="number"
            label="Connection securities / guarantee"
            value={cc.securitiesEur ?? 0}
            onChange={setNumber("securitiesEur")}
            error={Boolean(errors.securitiesEur)}
            helperText={errors.securitiesEur || "Funded on day one in Sources & Uses"}
            InputProps={{ endAdornment: <InputAdornment position="end">EUR</InputAdornment> }}
            sx={{ minWidth: 260 }}
          />

          <FormControl size="small" sx={{ minWidth: 330 }}>
            <InputLabel>Treatment</InputLabel>
            <Select
              label="Treatment"
              value={cc.securitiesTreatment ?? "forfeited"}
              onChange={set("securitiesTreatment")}
            >
              {SECURITIES_TREATMENT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{selectedTreatment.description}</FormHelperText>
          </FormControl>

          <TextField
            size="small"
            type="number"
            label="COD delay scenario"
            value={cc.codDelayScenarioMonths ?? 0}
            onChange={setNumber("codDelayScenarioMonths")}
            error={Boolean(errors.codDelayScenarioMonths)}
            helperText={
              errors.codDelayScenarioMonths ||
              "Adds a sensitivity row. Cash flows are shifted, not written off."
            }
            InputProps={{ endAdornment: <InputAdornment position="end">months</InputAdornment> }}
            sx={{ minWidth: 240 }}
          />
        </Box>

        {notices.map((notice, index) => (
          <Alert key={index} severity={notice.severity} sx={{ mb: 1 }}>
            {notice.text}
          </Alert>
        ))}

        {regulatoryCapEnabled && (
          <Alert severity="info" sx={{ mb: 1 }}>
            {REGULATORY_CAP_NOTE}
          </Alert>
        )}

        <Alert severity={applied > 0 ? "success" : "info"} sx={{ mt: 1 }}>
          <strong>What the report will do:</strong> {outcome}
        </Alert>
      </AccordionDetails>
    </Accordion>
  );
}
